"use client";

import { Check, ChevronDown, Cpu, LoaderCircle, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Popover } from "@/components/ui-system";
import type { DeviceDto } from "@/lib/api/types";
import { getDeviceSecondaryIdentifier } from "@/lib/devices/device-field-helpers";
import { cn } from "@/lib/utils/cn";

export type HeaderDeviceSelectProps = {
  devices: DeviceDto[];
  value: string;
  onChange: (deviceId: string) => void;
  loading?: boolean;
  disabled?: boolean;
};

function deviceLabel(device: DeviceDto | null | undefined): string {
  return device?.name?.trim() || device?.device_uid || "Select logger";
}

export function HeaderDeviceSelect({ devices, value, onChange, loading = false, disabled = false }: HeaderDeviceSelectProps) {
  const [query, setQuery] = useState("");
  const selected = useMemo(() => devices.find((device) => device.id === value) ?? null, [devices, value]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return devices.filter((device) => !needle || `${device.name ?? ""} ${device.device_uid}`.toLowerCase().includes(needle)).slice(0, 80);
  }, [devices, query]);
  const isDisabled = disabled || loading || devices.length === 0;

  return (
    <Popover
      placement="bottom-end"
      ariaLabel="Select data logger"
      disabled={isDisabled}
      contentClassName="w-[min(22rem,calc(100vw-2rem))] p-0"
      trigger={
        <span className={cn("ui-control header-device-select-trigger inline-flex h-10 min-h-10 w-10 min-w-10 items-center justify-center gap-ds-2 px-ds-1.5 py-ds-1.5 sm:w-auto sm:min-w-[12rem] sm:justify-start sm:px-ds-2", isDisabled && "opacity-55")}>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-accent-muted text-accent"><Cpu className="h-4 w-4" aria-hidden /></span>
          <span className="min-w-0 flex-1 text-left"><span className="block text-micro uppercase tracking-[0.08em] text-muted">Device</span><span className="block truncate text-caption font-semibold">{loading ? "Loading…" : deviceLabel(selected)}</span></span>
          {loading ? <LoaderCircle className="h-4 w-4 animate-spin text-muted" aria-hidden /> : <ChevronDown className="h-4 w-4 text-muted" aria-hidden />}
        </span>
      }
    >
      {(close) => (
        <>
          <div className="border-b border-card-border p-ds-2">
            <div className="flex min-h-10 items-center gap-ds-2 rounded-control border border-card-border bg-surface-inset px-ds-3">
              <Search className="h-4 w-4 text-muted" aria-hidden />
              <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search data loggers" className="min-w-0 flex-1 border-0 bg-transparent text-caption text-foreground outline-none placeholder:text-muted" />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto p-ds-2" role="listbox" aria-label="Data logger">
            {filtered.length ? filtered.map((device) => {
              const active = device.id === value;
              return (
                <button key={device.id} type="button" role="option" aria-selected={active} onClick={() => { onChange(device.id); setQuery(""); close(); }} className={cn("flex w-full items-center gap-ds-2 rounded-control px-ds-3 py-ds-2 text-left transition-colors duration-fast", active ? "bg-accent-muted text-foreground" : "text-muted hover:bg-surface-hover hover:text-foreground")}>
                  <span className="min-w-0 flex-1"><span className="block truncate text-caption font-semibold">{deviceLabel(device)}</span><span className="block truncate text-micro text-muted">{getDeviceSecondaryIdentifier(device)}</span></span>
                  {active ? <Check className="h-4 w-4 text-accent" aria-hidden /> : null}
                </button>
              );
            }) : <p className="px-ds-3 py-ds-5 text-center text-caption text-muted">No devices found.</p>}
          </div>
        </>
      )}
    </Popover>
  );
}
